import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui';
import { useAuthStore } from '../../store/useAuthStore';
import { ApiError } from '../../services/api/apiClient';
import { BECAS_ACADEMY_SLUG } from '../../constants/becas';

const inputClass =
  'w-full rounded-xl bg-brand-darkNavy border border-brand-lightBlue/40 pl-12 pr-4 py-3 text-white placeholder:text-dark-500 focus:outline-none focus:border-brand-yellow transition-colors';

export function BecasAuthForm() {
  const { t } = useTranslation();
  const { login, register, isLoading } = useAuthStore();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (mode === 'login') {
        await login({ email, password });
      } else {
        await register({
          email,
          password,
          role: 'player',
          academy_id: BECAS_ACADEMY_SLUG,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) setError(t('becas.auth.invalidCredentials'));
        else setError(err.message || t('becas.errors.generic'));
      } else {
        setError(t('becas.errors.connection'));
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex rounded-xl bg-brand-darkNavy/80 border border-brand-lightBlue/30 p-1 mb-2">
        {(['login', 'register'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => {
              setMode(tab);
              setError(null);
            }}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
              mode === tab
                ? 'bg-brand-yellow text-brand-darkNavy'
                : 'text-dark-400 hover:text-white'
            }`}
          >
            {t(`becas.auth.${tab}Tab`)}
          </button>
        ))}
      </div>

      {mode === 'register' && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t('becas.profile.firstName')}
              className={inputClass}
              required
              minLength={2}
              autoComplete="given-name"
            />
          </div>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t('becas.profile.lastName')}
              className={inputClass}
              required
              minLength={2}
              autoComplete="family-name"
            />
          </div>
        </div>
      )}

      <div className="relative">
        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className={inputClass}
          required
          autoComplete="email"
        />
      </div>

      <div className="relative">
        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
        <input
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t('becas.auth.password')}
          className={`${inputClass} pr-12`}
          required
          minLength={8}
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-dark-400 hover:text-white"
          aria-label={showPassword ? t('becas.auth.hidePassword') : t('becas.auth.showPassword')}
        >
          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>

      {error && (
        <p className="text-sm text-accent-rose bg-accent-rose/10 border border-accent-rose/30 rounded-xl px-4 py-3">
          {error}
        </p>
      )}

      <Button type="submit" variant="primary" fullWidth isLoading={isLoading}>
        {mode === 'login' ? t('becas.auth.signIn') : t('becas.auth.createAccount')}
      </Button>
    </form>
  );
}
